/**
 * 🔴 **THE way CORE produces a word a customer reads.** `copy("payment.tile.upi")`.
 *
 * Exactly the shape of `money()` in `@rista/core/currency`, and for exactly the same reason: the value
 * depends on merchant-and-customer runtime state, and core can read the store directly.
 * `money()` resolves the symbol, the precision and the grouping locale; this resolves the
 * LANGUAGE. A store-backed caller changes no signature to use it.
 *
 * ## Why core has a catalogue at all
 *
 * `packages/ui/kiosk/copy` is the catalogue for words a SCREEN draws, and its rule — "a screen
 * holds no literal" — was always satisfied: no kiosk screen has ever had English in it. The copy
 * in `./en` arrives at those screens as PROPS, from core, which is underneath the boundary that
 * rule guards. Core cannot use the kiosk's catalogue, because `packages/core` must not import
 * `@rista/ui` (audit check E). So it owns this one.
 *
 * ## Which of the two to use
 *
 * | your module | what to do |
 * |---|---|
 * | already imports the store (a hook, an `index.ts`) | `import { copy }` from here and call it |
 * | **PURE** (a model, a selector, anything with a store-free test) | take a `CoreCopy` **argument**, exactly as you already take a `MoneyFormat` — see `./resolve` |
 *
 * 🔴 **A pure module must not import THIS file.** It pulls the store, and the store pulls
 * `utils/navigation`'s `createBrowserHistory()` — `document is not defined` in plain node. That
 * is not hypothetical: it broke twelve model suites the first time this module existed without
 * the split. Identical hazard, identical remedy, as `@rista/core/currency/format.ts` documents.
 *
 * ## Why the POS is unaffected
 *
 * `selectKioskLanguage` answers "the customer's, else the merchant's, else `en`" — and a POS
 * terminal has no kiosk config and no customer override, so it answers `en`. Every shared module
 * (`discounts`, `loyalty`, `ordering/customer`) therefore returns exactly the English it
 * returned before, byte for byte.
 *
 * 🔴 **Never call this at module scope.** It reads the store, so a top-level
 * `export const MSG = copy("…")` would latch a language before rehydration and trip
 * `audit:uplift` checks H/I/J. The constants that used to hold sentences now hold KEYS, and the
 * resolution happens where the message is USED. Same rule `money()` already lives under.
 */
import store from "../redux-store";
import { selectKioskLanguage } from "../kiosk/language/selectors";
import { DEFAULT_LANGUAGE } from "../utils/i18n";

import { type CoreCopyKey, resolveCoreCopy } from "./resolve";
import type { CopyParams } from "../utils/i18n";

export * from "./resolve";

/** Resolve one key in the language this device is running in. */
export function copy(key: CoreCopyKey, params?: CopyParams): string {
  let language = DEFAULT_LANGUAGE;
  try {
    language = selectKioskLanguage(store.getState()) || DEFAULT_LANGUAGE;
  } catch {
    // A store that is not up yet answers English rather than throwing. Reading copy must never
    // be the thing that takes a terminal down.
  }
  return resolveCoreCopy(key, params, language);
}
